import { useMemo } from 'react';
import Button from 'components/commercetools-ui/atoms/button';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import { Reference } from 'types/reference';

export interface MembershipCtaProps {
  image: ImageProps;
  title: string;
  subtitle: string;
  ctaLabel?: string;
  ctaReference?: Reference;
  isPriority: boolean;
  imageQuality: number;
}

const MembershipCta: React.FC<MembershipCtaProps> = ({
  image,
  title,
  subtitle,
  ctaLabel,
  ctaReference,
  isPriority,
  imageQuality,
}) => {
  const cropFocus = useMemo(() => {
    if (image?.gravity?.coordinates && image.media?.width && image.media?.height) {
      const right = Math.ceil((image.gravity.coordinates.x / (image.media.width as number)) * 100);
      const top = Math.ceil((image.gravity.coordinates.y / (image.media.height as number)) * 100);

      return `right ${right.toString()}% top ${top.toString()}%`;
    }

    if (image?.gravity?.mode === 'center') {
      return 'center';
    }

    return 'initial';
  }, [image]);

  return (
    <section className="w-full overflow-visible px-16 pb-24 pt-0 md:px-48">
      <div className="mt-[clamp(150px,calc(250px-10vw-2vh),220px)] overflow-visible sm:mt-160 md:mt-[5vw]">
        <div className="mx-auto flex max-w-full flex-col items-center gap-8 overflow-visible rounded-3xl bg-neutral-100 p-16 md:max-h-380 md:max-w-[70vw] md:flex-row md:justify-center md:gap-24">
          {image && (
            <div className="min-h-280 w-full min-w-0 flex-1 -translate-y-140 pb-8 md:min-h-0 md:-translate-x-40 md:-translate-y-90">
              <div className="relative ml-30 h-320 overflow-hidden sm:h-400 md:h-[500px]">
                <Image
                  {...image}
                  alt={title}
                  fill
                  sizes={image.sizes ?? '(max-width: 640px) 80vw, (max-width: 1007px) 70vw, 30vw'}
                  style={{ objectFit: 'contain', objectPosition: cropFocus }}
                  className="mx-auto block object-contain"
                  priority={isPriority}
                  fetchPriority={isPriority ? 'high' : 'auto'}
                  quality={imageQuality}
                />
              </div>
            </div>
          )}

          <div className="-mt-84 flex min-w-0 flex-1 flex-col gap-24 px-16 pt-8 md:-mt-2 md:p-16">
            <h1 className="m-0 text-40 font-bold leading-[44px] tracking-normal text-black md:text-48 md:leading-[52px] lg:text-52 lg:leading-[58px]">
              {title}
            </h1>

            {subtitle && (
              <div className="text-17 leading-[26px] text-gray-700 md:text-18 md:leading-[28px]">
                <p>{subtitle}</p>
              </div>
            )}

            {ctaLabel && (
              <div>
                <Link link={ctaReference}>
                  <Button
                    variant="ghost"
                    size="fit"
                    className="flex w-full items-center justify-center rounded-2xl border-2 border-black bg-black px-40 py-14 text-12 font-bold uppercase tracking-[0.25em] text-white hover:bg-gray-900"
                  >
                    {ctaLabel}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MembershipCta;
